import { forwardRef, ButtonHTMLAttributes } from 'react';
import { motion } from 'framer-motion';
import { useUIV2Safe } from '@/contexts/UIV2Context';
import { useReduceMotion } from '@/hooks/useReduceMotion';
import { cn } from '@/lib/utils';
import { Slot } from '@radix-ui/react-slot';

export interface NeonButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'outline' | 'ghost' | 'destructive';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  glowIntensity?: 'low' | 'medium' | 'high';
  asChild?: boolean;
}

const sizeClasses = {
  default: 'h-10 px-4 py-2',
  sm: 'h-9 px-3',
  lg: 'h-11 px-8',
  icon: 'h-10 w-10',
};

const variantClasses = {
  default: 'bg-primary text-primary-foreground',
  outline: 'border border-primary/50 bg-transparent text-primary',
  ghost: 'bg-transparent text-foreground hover:bg-primary/10',
  destructive: 'bg-destructive text-destructive-foreground',
};

/**
 * NeonButton - Glowing button with neon effects
 * Extends shadcn button with V2 styling
 */
export const NeonButton = forwardRef<HTMLButtonElement, NeonButtonProps>(
  function NeonButton(
    {
      className,
      variant = 'default',
      size = 'default',
      glowIntensity = 'medium',
      asChild = false,
      disabled,
      children,
      ...props
    },
    ref
  ) {
    const { isLowFx, uiV2Enabled } = useUIV2Safe();
    const reduceMotion = useReduceMotion();
    
    const Comp = asChild ? Slot : 'button';
    
    const glowStyles = {
      low: 'hover:shadow-[0_0_15px_hsl(var(--primary)/0.25)]',
      medium: 'hover:shadow-[0_0_25px_hsl(var(--primary)/0.35)]',
      high: 'hover:shadow-[0_0_35px_hsl(var(--primary)/0.5)]',
    };
    
    const baseClasses = cn(
      'inline-flex items-center justify-center gap-2 whitespace-nowrap',
      'rounded-lg text-sm font-medium',
      'transition-all duration-200',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      'disabled:pointer-events-none disabled:opacity-50',
      sizeClasses[size],
      variantClasses[variant],
      // V2 effects
      uiV2Enabled && !isLowFx && !disabled && glowStyles[glowIntensity],
      uiV2Enabled && !isLowFx && variant === 'default' && 'hover:brightness-110',
      uiV2Enabled && !isLowFx && variant === 'outline' && 'hover:bg-primary/10 hover:border-primary',
      className
    );
    
    // Use motion variant when effects are enabled
    if (uiV2Enabled && !isLowFx && !reduceMotion && !disabled) {
      return (
        <motion.button
          ref={ref}
          className={baseClasses}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          disabled={disabled}
        >
          {children}
        </motion.button>
      );
    }
    
    return (
      <Comp
        ref={ref}
        className={baseClasses}
        disabled={disabled}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);
